import * as cssDefs from './toastCssDefs';
import { AnimatePresence, motion } from 'framer-motion';
import { IconButton, Link, Typography } from 'bento';
import { IconCheckmarkCircle, IconCrossSmall, IconExclamationCircle } from '@tocktix/pantry';
import { Toast as ToastType } from '@tock/toast';
import { createPortal } from 'react-dom';
import { getToastSelector } from 'selectors/toastSelectors';
import { useActions } from '@tock/shared/utils/connect';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

const Toast: React.FC<ToastType> = ({ id, message, type, linkText, onClickLink, onClear }) => {
  const { actions } = useActions();
  const width = 320;
  const infoTimeout = 5000;
  const errorTimeout = 8000;

  const icon =
    type === 'info' ? (
      <IconCheckmarkCircle color="fgSuccessBase" />
    ) : (
      <IconExclamationCircle color="fgDangerBase" />
    );

  useEffect(() => {
    const timerId = setTimeout(
      () => {
        actions.toast.remove(id);
        onClear?.();
      },
      type === 'info' ? infoTimeout : errorTimeout
    );
    return () => clearTimeout(timerId);
  }, [actions.toast, id, onClear, type]);

  if (!message) {
    return null;
  }

  return (
    <motion.li
      data-testid="toast"
      layout
      initial={{ y: 10, scale: 0.75, opacity: 0 }}
      animate={{ y: 0, scale: 1, opacity: 1 }}
      key={id}
      transition={{
        type: 'spring',
        mass: 0.5,
        damping: 20,
        stiffness: 275,
      }}
      exit={{
        opacity: 0,
        scale: 0.85,
        transition: {
          duration: 0.2,
        },
      }}
      css={cssDefs.toastItem(width, type)}
    >
      <div css={cssDefs.iconContainer}>{icon}</div>
      <div css={cssDefs.typeContainer}>
        <Typography variant="body" css={cssDefs.messageTxt}>
          {message}
          &nbsp;
          {linkText && (
            <Link
              css={cssDefs.linkText}
              color="inherit"
              onClick={(e) => {
                e.preventDefault();
                onClickLink?.();
              }}
            >
              {linkText}
            </Link>
          )}
        </Typography>
      </div>
      <div css={cssDefs.dismissContainer}>
        <IconButton
          color="contrast"
          aria-label="close"
          onClick={() => {
            actions.toast.remove(id);
            onClear?.();
          }}
          css={cssDefs.dismissBtn}
        >
          <IconCrossSmall />
        </IconButton>
      </div>
    </motion.li>
  );
};

const Toaster = () => {
  const toasts = useSelector(getToastSelector);

  return (
    <>
      {createPortal(
        <ul data-testid="toast-container" css={cssDefs.toastsContainer}>
          <AnimatePresence>
            {toasts.map((t) => (
              <Toast {...t} key={t.id} />
            ))}
          </AnimatePresence>
        </ul>,
        document.body
      )}
    </>
  );
};

export default Toaster;
